import {
  calendarDateTimeFromTimestamp,
  CsvTableType,
  CustomLabel,
  DocumentCategoryWithoutReadIdAndClientId,
  DownloadFoldersInput,
  EntityType,
  ExtendedSearchModel,
  FolderEntity,
  FolderSortInvoiceOptions,
  getDownloadZipFileUrl as GetDownloadZipFileUrlMutation,
  GetDownloadZipFileUrlMutationVariables,
  getPublicFileKey,
  Language,
  ROOT_FOLDER_ID,
  SignatureDisplayStatus,
  StatusResult,
  UNIT_OWNER_TABLE_NAME,
  UploadFile,
  getTableClientId,
  getTypedLanguage,
  isInstantAfterOrEqual,
  isNilOrEmpty,
  todayDateTime,
} from '@rentguru/commons-utils';
import { mutation } from '@up2rent/graphql';
import { useLiveQuery } from 'dexie-react-hooks';
import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import last from 'lodash/last';
import { createContext, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import { IntlFormatters } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import { downloadData } from 'src/auth/storage';
import {
  filterFoldersForManagementFilters,
  getFolderSize,
  useQuery,
} from 'src/components/FoldersWithDB/Components/utils/folderUtils';
import {
  getFolderDepthString,
  getTranslatedLabel,
} from 'src/components/FoldersWithDB/Components/utils/folderWithDBUtils';
import { fetchBuildingsForFolders } from 'src/hooks/BuildingsContext';
import { fetchChargesForFolders, useCharges } from 'src/hooks/ChargesContext';
import { FetchByType } from 'src/hooks/CommunicationsContext';
import { useConfirmationDialog } from 'src/hooks/ConfirmationDialogContext';
import { useConnection } from 'src/hooks/ConnectionContext';
import { fetchContactsForFolders, fetchUnitOwnersForFolders } from 'src/hooks/ContactsContext';
import { useDocumentCategory } from 'src/hooks/FileCategoryContext';
import { useFiles } from 'src/hooks/FilesContext';
import { useFilterGroups } from 'src/hooks/FilterGroupsContext';
import { fetchInvoicesForFolders } from 'src/hooks/InvoicesContext';
import { useSignatureDocuments } from 'src/hooks/SignatureDocumentContext';
import { fetchTechnicsForFolders, useTechnics } from 'src/hooks/TechnicsContext';
import { fetchUnitLeasesForFolders } from 'src/hooks/UnitLeaseContext';
import { fetchUnitsForFolders } from 'src/hooks/UnitsContext';
import { useUser } from 'src/hooks/UserContext';
// eslint-disable-next-line import/default
import workerUrl from 'src/hooks/WebWorkers/FolderWorker?worker&url';
import { getFolderToAddWithTargetFolder } from 'src/hooks/WebWorkers/utils/folderSyncingUtils';
import { useWebWorker } from 'src/hooks/WebWorkers/WebWorkerHook';

import {
  FolderDatabaseAction,
  FolderDatabaseModel,
  FolderIndexes,
  UniqueFolderNameAndUploadFilesInput,
  calculateEmptyFolders,
  checkForEmptyFoldersAfterAction,
  getCategoryToWorkWith,
  getFolderDepthForNavigation,
  getFoldersAtBaseEntityLevel,
  getUniqueCategoryParameter,
  getUniqueFolderNameAndCreateFiles,
  removeDuplicatesById,
  syncCategoryChanges,
  syncFileChanges,
} from './folderContextUtils';
import {
  DB_FOLDERS,
  getLastSyncedAt,
  resetDatabase,
  setLastSyncedAt,
  useFolderDatabase,
} from './FolderDatabaseContext';
import { FolderWithDBState, folderWithDBReducer, initialState, type FolderFilters } from './FolderWithDBReducer';

export interface FolderWithDBContext extends FolderWithDBState {
  getCurrentFoldersByDepth: (folderDepth: string) => Promise<FolderDatabaseModel[]>;
  addFolderNavigation: (folder: FolderDatabaseModel | undefined) => void;
  setFolderNavigationByFolderId: (folderId: string[]) => Promise<void>;
  addExternalFiles: (
    formatMessage: IntlFormatters['formatMessage'],
    files: File[],
    targetFolderCategoryId?: string,
    labels?: CustomLabel[]
  ) => Promise<void>;
  createNewFolder: (labels: CustomLabel[], targetFolderCategoryId?: string) => Promise<void>;
  setUploadFiles: (files: UploadFile[]) => void;
  deleteFolders: (
    foldersToDelete: FolderDatabaseModel[],
    softDelete?: boolean,
    notDeleteFromCurrentFolders?: boolean
  ) => Promise<void>;
  changeVisibilityAllFolders: (
    recalculate?: boolean,
    categoriesToCheck?: DocumentCategoryWithoutReadIdAndClientId[]
  ) => Promise<void>;
  setExtendedSearchModel: (model: ExtendedSearchModel) => void;
  clickOnSearchFolder: (folder: FolderDatabaseModel) => void;
  setSearchFolders: (folders: FolderDatabaseModel[], newExtendedSearchModel?: ExtendedSearchModel) => void;
  cancelSearch: () => Promise<void>;
  cleanUp: () => void;
  setIsBusy: (isBusy: boolean) => void;
  setOnPage: (onPage: boolean) => void;
  setSelectedFolders: (folders: FolderDatabaseModel[]) => void;
  renameFolder: (folder: FolderDatabaseModel, file: boolean) => Promise<void>;
  moveFolders: (folders: FolderDatabaseModel[], targetFolderCategoryId: string) => Promise<void>;
  calculateNewFolders: () => Promise<void>;
  updateFolderCategory: (updatedCategory: DocumentCategoryWithoutReadIdAndClientId) => Promise<void>;
  setDraggingFolders: (draggingFolders: string[]) => void;
  setSortOnType: (sortType: string) => void;
  resetSortOnType: () => void;
  setSortOnInvoicePaid: (sortType: FolderSortInvoiceOptions) => void;
  resetSortOnInvoicePaid: () => void;
  setSortOnSignatureStatus: (signatureStatus: SignatureDisplayStatus) => void;
  resetSortOnSignatureStatus: () => void;
  applyFolderFilters: (folderFilters: FolderFilters) => void;
  setListView: (listView: boolean) => void;
  calculateDepthByUrl: (
    entityDepth: FolderDatabaseModel[],
    folderDepth?: string[],
    newUrlDepth?: string | null
  ) => Promise<void>;
  exportData: (
    selectedEntities?: CsvTableType[]
  ) => Promise<{ result?: StatusResult; zippedData?: Record<string, Uint8Array> }>;
  setLocalBusy: (localBusy: boolean) => void;
}

export const FolderWithDBContext = createContext<FolderWithDBContext | null>(null);

export const FolderWithDBContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { result, error: workerError, isLoading: webWorkerLoading, runTask } = useWebWorker(workerUrl);
  const [state, dispatch] = useReducer(folderWithDBReducer, initialState);
  const { clientId, userId, language } = useUser();
  const { isInFocus } = useConnection();
  const {
    isDbConnected,
    getFoldersByIndex,
    bulkAddFolders,
    bulkDeleteFolders,
    getFolderById,
    updateDatabaseFolder,
    getFoldersWithCustomFilter,
    getFoldersByDepthRange,
    getAllRecords,
    bulkUpdateDatabaseFolders,
    deleteItemsFromFoldersToSync,
  } = useFolderDatabase();
  const {
    fileCategories,
    getDocumentCategoryById,
    createFileCategory,
    deleteMultipleFilesCategoriesAtOnce,
    updateFileCategory,
    fetchDocumentCategories,
  } = useDocumentCategory();
  const { createFile, deleteFile, fetchFileFromId, fetchFilesByDate } = useFiles();
  const { getConfirmation } = useConfirmationDialog();
  const { getSignatureDocumentForFile, updateSignatureDocument } = useSignatureDocuments();
  const { getTechnic, technicsLoading } = useTechnics();
  const { charges, error: chargesError } = useCharges();
  const chargesPending = isNil(charges) && isNil(chargesError);
  const { selectedManagementFilterGroups } = useFilterGroups();
  const history = useHistory();
  const query = useQuery();

  const watchCurrentFoldersChange = useLiveQuery(
    () =>
      DB_FOLDERS.folders
        .where(FolderIndexes.DEPTH)
        .equals(getFolderDepthForNavigation(last(state.folderNavigation)))
        .toArray(),
    [state.folderNavigation]
  );

  const foldersToSync = useLiveQuery(() => DB_FOLDERS.foldersToSync.toArray());

  const failedSyncHash = useRef<string | null>(null);
  const foldersToSyncHash = useMemo(
    () =>
      foldersToSync
        ?.map((f) => f.id)
        .sort()
        .join('|') ?? '',
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [foldersToSync?.length]
  );

  useEffect(() => {
    const applyWorkerResult = async (foldersToAdd: FolderDatabaseModel[], foldersToRemove: FolderDatabaseModel[]) => {
      if (!isEmpty(foldersToRemove)) {
        await bulkDeleteFolders(foldersToRemove.map((folder) => folder.id));
      }
      if (!isEmpty(foldersToAdd)) {
        await bulkAddFolders(foldersToAdd);
      }
      await setLastSyncedAt(todayDateTime());
      if (state.onPage) {
        await calculateDepthByUrl([]);
      }
      dispatch({ type: 'SET_INITIALIZED', payload: true });
      dispatch({ type: 'INCREMENT_FOLDER_DB_VERSION' });
    };
    if (workerError) {
      console.error('Web Worker error:', workerError);
    }
    const realResult = result as {
      folders: FolderDatabaseModel[];
      foldersToDelete: FolderDatabaseModel[];
      processed: boolean;
      timestamp: number;
    };
    if (realResult) {
      const { folders, foldersToDelete: toRemove } = realResult;
      if (!isEmpty(folders) || !isEmpty(toRemove)) {
        applyWorkerResult(folders ?? [], toRemove ?? []);
      } else {
        dispatch({ type: 'SET_INITIALIZED', payload: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerError, result, webWorkerLoading]);

  useEffect(() => {
    if (isInFocus) {
      failedSyncHash.current = null;
    }
  }, [isInFocus]);

  useEffect(() => {
    if (
      !isInFocus ||
      state.isSyncing ||
      webWorkerLoading ||
      technicsLoading ||
      chargesPending ||
      !clientId ||
      failedSyncHash.current === foldersToSyncHash
    ) {
      return;
    }
    const syncFoldersInOrder = async () => {
      if (isNil(foldersToSync) || isNil(clientId)) {
        return;
      }
      const foldersToSyncInOrder = [...foldersToSync].sort((a, b) => {
        return (a.syncTimestamp ?? 0) - (b.syncTimestamp ?? 0);
      });

      const fileCategoriesToWorkWith = await fetchDocumentCategories();
      for (const folderToSync of foldersToSyncInOrder) {
        const { file, category, folderDatabaseAction } = folderToSync;

        if (file) {
          await syncFileChanges(
            file,
            folderDatabaseAction,
            folderToSync.id,
            charges ?? undefined,
            fileCategoriesToWorkWith,
            getFoldersByIndex,
            getFoldersWithCustomFilter,
            getFoldersByDepthRange,
            getDocumentCategoryById,
            getTechnic,
            bulkAddFolders,
            bulkDeleteFolders,
            deleteItemsFromFoldersToSync,
            clientId
          );
        }

        if (category) {
          await syncCategoryChanges(
            category,
            folderDatabaseAction,
            folderToSync.id,
            getFoldersWithCustomFilter,
            getFoldersByIndex,
            getDocumentCategoryById,
            updateDatabaseFolder,
            bulkAddFolders,
            bulkUpdateDatabaseFolders,
            deleteItemsFromFoldersToSync,
            getFoldersByDepthRange,
            bulkDeleteFolders
          );
        }
      }

      dispatch({
        type: 'SET_BLOCKED_FOLDERS',
        payload: [],
      });
    };
    const syncFolders = async () => {
      if (!isDbConnected || isNilOrEmpty(foldersToSync)) {
        return;
      }
      dispatch({ type: 'SET_IS_SYNCING', payload: true });
      try {
        await syncFoldersInOrder();
      } catch (error) {
        console.error('[folders] syncing the folders failed', error);
        failedSyncHash.current = foldersToSyncHash;
      } finally {
        dispatch({ type: 'SET_IS_SYNCING', payload: false });
      }
    };
    syncFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foldersToSyncHash, state.isSyncing, webWorkerLoading, isInFocus, technicsLoading, chargesPending]);

  useEffect(() => {
    const newFolders = async () => {
      if (webWorkerLoading) {
        return;
      }
      try {
        await calculateNewFolders();
      } catch (error) {
        console.error('Error calculating new folders:', error);
        dispatch({ type: 'SET_INITIALIZED', payload: true });
      } finally {
        setIsBusy(false);
      }
    };
    if (isDbConnected) {
      newFolders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDbConnected]);

  useEffect(() => {
    if (!isInFocus) return;
    if (!isEmpty(foldersToSyncHash)) return;
    if (!isDbConnected || state.isSyncing) return;
    if (!state.onPage && state.folderNavigation.length === 1) return;
    dispatch({ type: 'SET_IS_BUSY', payload: true });
    dispatch({ type: 'SET_SELECTED_FOLDERS', payload: [] });
    const fetchFolders = async () => {
      try {
        const lastFolderInNavigation = last(state.folderNavigation);
        const folders = await getCurrentFoldersByDepth(getFolderDepthForNavigation(lastFolderInNavigation));
        const foldersWithSize = await Promise.all(
          folders.map(async (folder) => {
            if (folder.folderEntity === FolderEntity.FOLDER) {
              const { size, updatedAt } = await getFoldersSizeAndDate(folder);
              return { ...folder, size, updatedAt };
            }
            return folder;
          })
        );
        const filteredFoldersWithSize = !isNilOrEmpty(selectedManagementFilterGroups)
          ? filterFoldersForManagementFilters(foldersWithSize, selectedManagementFilterGroups)
          : foldersWithSize;
        if (state.seeAllFolders && !isNil(lastFolderInNavigation)) {
          const emptyFolders = calculateEmptyFolders(last(state.folderNavigation)!, fileCategories, folders);
          dispatch({ type: 'SET_CURRENT_FOLDERS', payload: [...filteredFoldersWithSize, ...emptyFolders] });
        } else {
          dispatch({ type: 'SET_CURRENT_FOLDERS', payload: filteredFoldersWithSize });
        }
      } catch (error) {
        console.error('Error fetching folders:', error);
      } finally {
        dispatch({ type: 'SET_IS_BUSY', payload: false });
      }
    };
    if (!state.pauseFetching && !webWorkerLoading) {
      fetchFolders();
    } else {
      dispatch({ type: 'SET_IS_BUSY', payload: false });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.folderNavigation,
    isDbConnected,
    webWorkerLoading,
    state.onPage,
    watchCurrentFoldersChange,
    state.pauseFetching,
    selectedManagementFilterGroups,
    fileCategories,
    foldersToSyncHash,
    state.isSyncing,
    isInFocus,
  ]);

  useEffect(() => {
    const unListen = history.listen((_location: Location, action: string) => {
      if (action === 'POP') {
        const newUrlDepth = new URLSearchParams(location?.search || '').get('depth');
        if (state.onPage) {
          calculateDepthByUrl([], undefined, newUrlDepth);
        }
      }
    });
    return () => {
      unListen();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, state.folderNavigation]);

  const getFoldersSizeAndDate = async (
    folder: FolderDatabaseModel
  ): Promise<{ size: number; updatedAt: string | undefined }> => {
    const foldersByDepth = (await getFoldersByDepthRange(`${folder.depth}.${folder.documentCategoryId}`)).filter(
      (folderByDepth) => folderByDepth.folderEntity === FolderEntity.FILE
    );
    const totalSize = getFolderSize(foldersByDepth);
    let lastUpdated: string | undefined | number = 0;
    if (foldersByDepth.length > 0) {
      lastUpdated = foldersByDepth.reduce((latest, folderByDepth) => {
        if (!latest) return folderByDepth.updatedAt;
        if (!folderByDepth.updatedAt) return latest;
        return isInstantAfterOrEqual(
          calendarDateTimeFromTimestamp(folderByDepth.updatedAt),
          calendarDateTimeFromTimestamp(latest)
        )
          ? folderByDepth.updatedAt
          : latest;
      }, foldersByDepth[0]?.updatedAt);
    } else {
      lastUpdated = undefined;
    }
    return { size: totalSize, updatedAt: lastUpdated };
  };

  const getCurrentFoldersByDepth = async (folderDepth: string): Promise<FolderDatabaseModel[]> => {
    try {
      const folders = await getFoldersByIndex(FolderIndexes.DEPTH, folderDepth);
      return folders;
    } catch (error) {
      console.error('Error fetching folders:', error);
      return [];
    }
  };

  const addFolderNavigation = (folder: FolderDatabaseModel | undefined) => {
    if (!folder && !isEmpty(state.entityDepth)) {
      dispatch({ type: 'SET_FOLDER_NAVIGATION', payload: [undefined, ...state.entityDepth] });
      return;
    }
    dispatch({ type: 'SET_LOCAL_BUSY', payload: true });
    if (!folder || state.folderNavigation.some((navigationFolder) => navigationFolder?.id === folder.id)) {
      const index = state.folderNavigation.findIndex((folderNavigation) => folderNavigation?.id === folder?.id);
      const newNavigation = state.folderNavigation.slice(0, index + 1);
      dispatch({ type: 'SET_FOLDER_NAVIGATION', payload: newNavigation });
      return;
    }
    dispatch({ type: 'SET_FOLDER_NAVIGATION', payload: [...state.folderNavigation, folder] });
  };

  const setFolderNavigationByFolderId = async (folderId: string[]) => {
    dispatch({ type: 'SET_LOCAL_BUSY', payload: true });
    const folderNavigation: (FolderDatabaseModel | undefined)[] = [undefined];
    for (const id of folderId) {
      const folder = await getFolderById(id);
      if (folder) {
        folderNavigation.push(folder);
      }
    }
    dispatch({ type: 'SET_FOLDER_NAVIGATION', payload: folderNavigation });
  };

  const addExternalFiles = async (
    formatMessage: IntlFormatters['formatMessage'],
    files: File[],
    targetFolderCategoryId?: string,
    labels?: CustomLabel[]
  ) => {
    dispatch({ type: 'SET_IS_BUSY', payload: true });

    const lastFolderInNavigation = last(state.folderNavigation);
    if (!lastFolderInNavigation) {
      console.error('No last folder in navigation found.');
      dispatch({ type: 'SET_IS_BUSY', payload: false });
      return;
    }

    const entityId = lastFolderInNavigation.entityId;
    const foldersInTargetCategory = targetFolderCategoryId
      ? (await getFoldersByIndex(FolderIndexes.DOCUMENT_CATEGORY_ID, targetFolderCategoryId)).filter(
          (folder) => folder.folderEntity === FolderEntity.FILE && folder.entityId === entityId
        )
      : await getFoldersAtBaseEntityLevel(entityId, getFoldersByIndex);

    const currentLevelCategories = fileCategories.filter(
      (category) => category.entity === lastFolderInNavigation.categoryEntity
    );

    const { category: currentCategory } = await getCategoryToWorkWith(
      lastFolderInNavigation,
      currentLevelCategories,
      createFileCategory,
      getDocumentCategoryById,
      labels,
      targetFolderCategoryId
    );

    const inputs: UniqueFolderNameAndUploadFilesInput = {
      filesToUpload: files,
      filesInFolder: foldersInTargetCategory,
      entityType: lastFolderInNavigation?.categoryEntity as EntityType,
      entityId: entityId,
      currentCategory,
      skipCheck: !isEmpty(labels),
      createFile,
      deleteFile,
      fetchFileFromId,
      getConfirmation,
      formatMessage,
      getSignatureDocumentForFile,
      setUploadFiles,
    };

    const createdFiles = await getUniqueFolderNameAndCreateFiles(inputs);

    const newFolderModels = compact(createdFiles).map((file) =>
      getFolderToAddWithTargetFolder(file, lastFolderInNavigation, currentCategory.fileCategory)
    );
    if (!isEmpty(newFolderModels)) {
      dispatch({
        type: 'SET_CURRENT_FOLDERS',
        payload: removeDuplicatesById([...state.currentFolders, ...newFolderModels]),
      });
    }

    setUploadFiles([]);
    dispatch({ type: 'SET_IS_BUSY', payload: false });
  };

  const createNewFolder = async (labels: CustomLabel[], targetFolderCategoryId?: string) => {
    const lastFolderInNavigation = last(state.folderNavigation);
    if (isNil(lastFolderInNavigation)) {
      console.error('No last folder in navigation found.');
      return;
    }
    const parentCategoryId = targetFolderCategoryId ?? lastFolderInNavigation.documentCategoryId;
    const currentLevelCategories = fileCategories.filter(
      (category) => category.entity === lastFolderInNavigation.categoryEntity && category.parentId === parentCategoryId
    );
    const uniqueLabels = getUniqueCategoryParameter(labels!, currentLevelCategories);
    const newCategory = await createFileCategory(
      uniqueLabels,
      lastFolderInNavigation.categoryEntity as EntityType,
      targetFolderCategoryId
    );

    if (lastFolderInNavigation.documentCategoryId === targetFolderCategoryId || isNil(targetFolderCategoryId)) {
      changeVisibilityAllFolders(true, [...fileCategories, newCategory]);
    }
  };

  const setUploadFiles = (files: UploadFile[]) => {
    dispatch({ type: 'SET_UPLOADING_FILES', payload: files });
  };

  const changeVisibilityAllFolders = async (
    recalculate?: boolean,
    categoriesToCheck?: DocumentCategoryWithoutReadIdAndClientId[]
  ) => {
    const lastNavigationParentFolder = last(state.folderNavigation);
    if (state.seeAllFolders && !recalculate) {
      const folders = await getCurrentFoldersByDepth(getFolderDepthForNavigation(lastNavigationParentFolder));
      const foldersWithSize = await Promise.all(
        folders.map(async (folder) => {
          if (folder.folderEntity === FolderEntity.FOLDER) {
            const { size, updatedAt } = await getFoldersSizeAndDate(folder);
            return { ...folder, size, updatedAt };
          }
          return folder;
        })
      );
      dispatch({
        type: 'SET_CURRENT_FOLDERS',
        payload: !isNilOrEmpty(selectedManagementFilterGroups)
          ? filterFoldersForManagementFilters(foldersWithSize, selectedManagementFilterGroups)
          : foldersWithSize,
      });
    } else {
      if (!lastNavigationParentFolder) {
        return;
      }

      const foldersToAdd = calculateEmptyFolders(
        lastNavigationParentFolder!,
        categoriesToCheck ?? fileCategories,
        state.currentFolders
      );
      dispatch({
        type: 'SET_CURRENT_FOLDERS',
        payload: [...state.currentFolders, ...foldersToAdd],
      });
    }

    dispatch({ type: 'SET_SEE_ALL_FOLDERS', payload: !state.seeAllFolders || (recalculate ?? false) });
  };

  const deleteFolders = async (foldersToDelete: FolderDatabaseModel[], softDelete?: boolean) => {
    dispatch({ type: 'SET_BLOCKED_FOLDERS', payload: foldersToDelete.map((folder) => folder.id) });
    dispatch({ type: 'SET_IS_BUSY', payload: true });
    const uniqueSet = new Set<string>();

    const { categoriesToDelete, filesToDelete } = foldersToDelete.reduce<{
      categoriesToDelete: FolderDatabaseModel[];
      filesToDelete: FolderDatabaseModel[];
    }>(
      (acc, folder) => {
        if (folder.folderEntity === FolderEntity.FILE) {
          acc.filesToDelete.push(folder);
          folder.depthByFolderId.forEach((id) => uniqueSet.add(id));
        }
        if (folder.folderEntity === FolderEntity.FOLDER) {
          acc.categoriesToDelete.push(folder);
        }
        return acc;
      },
      { categoriesToDelete: [], filesToDelete: [] }
    );

    const realCategoriesToDelete: DocumentCategoryWithoutReadIdAndClientId[] = [];
    await Promise.all([
      ...filesToDelete.map(async (folder) => {
        try {
          const file = await fetchFileFromId(folder.documentId!);
          if (!isNilOrEmpty(file)) {
            await deleteFile(file[0]);
          }
        } catch (error) {
          console.error('Error fetching file for deletion:', error);
        }
      }),
      ...categoriesToDelete.map(async (folder) => {
        try {
          const documentCategory = await getDocumentCategoryById(folder.documentCategoryId);
          if (!isNilOrEmpty(documentCategory) && !softDelete) {
            realCategoriesToDelete.push(documentCategory);
          }
        } catch (error) {
          console.error('Error deleting document category:', error);
        }
      }),
    ]);

    if (!isNilOrEmpty(realCategoriesToDelete)) {
      const uniqueCategoriesToDelete = removeDuplicatesById(realCategoriesToDelete);
      await deleteMultipleFilesCategoriesAtOnce(uniqueCategoriesToDelete);
    }
    await bulkDeleteFolders(foldersToDelete.map((folder) => folder.id));

    const parentFoldersToDelete: string[] = [];
    for (const folderId of [...uniqueSet]) {
      const folder = await getFolderById(folderId);
      const folderToDelete = await checkForEmptyFoldersAfterAction(
        folder,
        getDocumentCategoryById,
        getFoldersWithCustomFilter,
        getFoldersByDepthRange
      );
      if (folderToDelete) {
        parentFoldersToDelete.push(folderToDelete);
      }
    }

    await bulkDeleteFolders(parentFoldersToDelete);

    dispatch({
      type: 'SET_CURRENT_FOLDERS',
      payload: state.currentFolders.filter((folder) => !foldersToDelete.some((toDelete) => toDelete.id === folder.id)),
    });

    dispatch({ type: 'SET_BLOCKED_FOLDERS', payload: [] });
    dispatch({ type: 'SET_IS_BUSY', payload: false });
  };

  const clickOnSearchFolder = async (folder: FolderDatabaseModel) => {
    dispatch({ type: 'SET_SEARCH_FOLDERS', payload: false });
    dispatch({ type: 'SET_LOCAL_BUSY', payload: true });
    const folderNavigation: (FolderDatabaseModel | undefined)[] = [undefined];
    for (const folderId of folder.depthByFolderId) {
      const folderInNavigation = await getFolderById(folderId);
      if (folderInNavigation && folderInNavigation.folderEntity !== FolderEntity.FILE) {
        folderNavigation.push(folderInNavigation);
      }
    }
    if (
      folder.folderEntity !== FolderEntity.FILE &&
      !folderNavigation.some((folderInNavigation) => folderInNavigation?.id === folder.id)
    ) {
      folderNavigation.push(folder);
    }
    dispatch({ type: 'SET_FOLDER_NAVIGATION', payload: folderNavigation });
  };

  const setSearchFolders = (folders: FolderDatabaseModel[], newExtendedSearchModel?: ExtendedSearchModel) => {
    const hasSearchCriteria =
      newExtendedSearchModel && Object.values(newExtendedSearchModel).some((value) => !isNil(value));
    if (isNilOrEmpty(folders) && !hasSearchCriteria) {
      cancelSearch();
      return;
    }
    dispatch({ type: 'SET_CURRENT_FOLDERS', payload: folders });
    dispatch({ type: 'SET_SEARCH_FOLDERS', payload: true });
  };

  const setExtendedSearchModel = (model: ExtendedSearchModel) => {
    dispatch({ type: 'SET_EXTENDED_SEARCH_MODEL', payload: model });
  };

  const cancelSearch = async () => {
    dispatch({ type: 'SET_SEARCH_FOLDERS', payload: false });
    dispatch({ type: 'SET_EXTENDED_SEARCH_MODEL', payload: {} });
    const folders = await getCurrentFoldersByDepth(getFolderDepthForNavigation(last(state.folderNavigation)));
    const foldersWithSize = await Promise.all(
      folders.map(async (folder) => {
        if (folder.folderEntity === FolderEntity.FOLDER) {
          const { size, updatedAt } = await getFoldersSizeAndDate(folder);
          return { ...folder, size, updatedAt };
        }
        return folder;
      })
    );
    if (state.seeAllFolders) {
      const foldersToAdd = calculateEmptyFolders(last(state.folderNavigation)!, fileCategories, foldersWithSize);
      dispatch({ type: 'SET_CURRENT_FOLDERS', payload: [...foldersWithSize, ...foldersToAdd] });
      return;
    }

    dispatch({
      type: 'SET_CURRENT_FOLDERS',
      payload: [...foldersWithSize],
    });
  };

  const cleanUp = () => {
    dispatch({ type: 'SET_FOLDER_NAVIGATION', payload: [undefined] });
    dispatch({ type: 'SET_CURRENT_FOLDERS', payload: [] });
    dispatch({ type: 'SET_ON_PAGE', payload: false });
  };

  const setIsBusy = (isBusy: boolean) => {
    dispatch({ type: 'SET_IS_BUSY', payload: isBusy });
  };

  const setOnPage = (onPage: boolean) => {
    dispatch({ type: 'SET_ON_PAGE', payload: onPage });
    if (onPage) dispatch({ type: 'SET_ENTITY_DEPTH', payload: [] });
  };

  const setSelectedFolders = (folders: FolderDatabaseModel[]) => {
    dispatch({ type: 'SET_SELECTED_FOLDERS', payload: folders });
  };

  const renameFolder = async (folder: FolderDatabaseModel, file: boolean) => {
    dispatch({ type: 'SET_BLOCKED_FOLDERS', payload: [folder.id] });
    if (file) {
      const fileToRename = await fetchFileFromId(folder.documentId!);
      const blobRes = await downloadData({ key: getPublicFileKey(fileToRename[0].key) }).result;
      const blobToCopy = await blobRes.body.blob();
      const newFileToUpload = new File([blobToCopy], encodeURIComponent(folder.translatedLabel!), {
        type: fileToRename[0].mimeType!,
      });

      await createFile(
        newFileToUpload,
        folder.categoryEntity as EntityType,
        folder.entityId,
        folder.documentCategoryId
      );

      await deleteFile(fileToRename[0]);
    } else {
      const currentCategory = getDocumentCategoryById(folder.documentCategoryId);
      if (isNil(currentCategory)) {
        console.error('Current category not found for ID:', folder.documentCategoryId);
        return;
      }

      const labelsInput =
        folder.labels?.map((label) => ({
          language: label.language,
          label: label.label,
        })) || [];

      await updateFileCategory(currentCategory, {
        labels: labelsInput,
      });
      const categoryFolders = await getFoldersWithCustomFilter(
        (dbFolder) =>
          dbFolder.documentCategoryId === currentCategory.id && dbFolder.folderEntity === FolderEntity.FOLDER
      );
      await Promise.all(
        categoryFolders.map((categoryFolder) => updateDatabaseFolder({ ...categoryFolder, labels: labelsInput }))
      );
      dispatch({
        type: 'SET_CURRENT_FOLDERS',
        payload: state.currentFolders.map((currentFolder) =>
          currentFolder.id === folder.id ? { ...currentFolder, labels: labelsInput } : currentFolder
        ),
      });
    }
  };

  const moveFolders = async (folders: FolderDatabaseModel[], targetFolderCategoryId: string) => {
    if (isEmpty(folders)) {
      return;
    }
    dispatch({ type: 'SET_BLOCKED_FOLDERS', payload: folders.map((folder) => folder.id) });
    dispatch({ type: 'SET_IS_BUSY', payload: true });

    try {
      const promises = folders.map(async (folder) => {
        if (folder.folderEntity === FolderEntity.FOLDER) {
          const currentCategory = getDocumentCategoryById(folder.documentCategoryId);
          const newParentId = isEmpty(targetFolderCategoryId) ? null : targetFolderCategoryId;
          await updateFileCategory(currentCategory, { parentId: newParentId });
          await syncCategoryChanges(
            { ...currentCategory, parentId: newParentId },
            FolderDatabaseAction.UPDATE,
            uuid(),
            getFoldersWithCustomFilter,
            getFoldersByIndex,
            getDocumentCategoryById,
            updateDatabaseFolder,
            bulkAddFolders,
            bulkUpdateDatabaseFolders,
            deleteItemsFromFoldersToSync,
            getFoldersByDepthRange,
            bulkDeleteFolders
          );
        }
        if (folder.folderEntity === FolderEntity.FILE) {
          const file = await fetchFileFromId(folder.documentId!);
          if (!isNil(file) && !isEmpty(file)) {
            const blobRes = await downloadData({ key: getPublicFileKey(file[0].key) }).result;
            const blobToCopy = await blobRes.body.blob();
            const newFileToUpload = new File([blobToCopy], encodeURIComponent(folder.translatedLabel!), {
              type: file[0].mimeType!,
            });

            await deleteFile(file[0]);
            await getFoldersByIndex(FolderIndexes.DOCUMENT_ID, file[0].id);
            const createdFile = await createFile(
              newFileToUpload,
              folder.categoryEntity as EntityType,
              folder.entityId,
              targetFolderCategoryId
            );
            if (!isNil(createdFile)) {
              const signatureDocument = getSignatureDocumentForFile(folder.documentId!);
              if (signatureDocument && createdFile) {
                await updateSignatureDocument({
                  id: signatureDocument?.id,
                  fileIds: signatureDocument.fileIds?.map((fileId) =>
                    fileId === folder.documentId ? createdFile.id : fileId
                  ),
                });
              }
            }
          }
        }
      });
      const results = await Promise.allSettled(promises);
      results.forEach((settledResult) => {
        if (settledResult.status === 'rejected') {
          throw settledResult.reason;
        }
      });
    } finally {
      dispatch({ type: 'SET_BLOCKED_FOLDERS', payload: [] });
      dispatch({ type: 'SET_IS_BUSY', payload: false });
    }
  };

  const calculateNewFolders = async () => {
    const currentClientId = clientId ?? '';
    const lastSync = await getLastSyncedAt();
    const isColdPath = isNil(lastSync);
    if (isColdPath) {
      await resetDatabase();
    }

    const fullDb = await getAllRecords();
    const isWarm = !isColdPath;

    const [newFiles, allFileCategories, contacts, unitOwners] = await Promise.all([
      fetchFilesByDate(isWarm ? lastSync : undefined),
      fetchDocumentCategories(),
      fetchContactsForFolders(currentClientId),
      fetchUnitOwnersForFolders(getTableClientId(clientId!, UNIT_OWNER_TABLE_NAME)),
    ]);

    if (isEmpty(allFileCategories) && !isEmpty(newFiles)) {
      dispatch({ type: 'SET_INITIALIZED', payload: true });
      return;
    }

    const [invoices, fetchedCharges, technics, buildings, units, leases] = isWarm
      ? [[], [], [], [], [], []]
      : await Promise.all([
          fetchInvoicesForFolders(FetchByType.BY_CLIENT, getTableClientId(clientId!, 'Invoice')),
          fetchChargesForFolders(currentClientId),
          fetchTechnicsForFolders(currentClientId),
          fetchBuildingsForFolders(currentClientId),
          fetchUnitsForFolders(currentClientId),
          fetchUnitLeasesForFolders(currentClientId),
        ]);

    runTask({
      files: newFiles,
      categories: allFileCategories,
      fullDb,
      invoices,
      buildings,
      units,
      leases,
      charges: fetchedCharges,
      unitOwners,
      technics,
      contacts,
      isIncrementalSync: isWarm,
    });
  };

  const setDraggingFolders = (draggingFolders: string[]) => {
    dispatch({ type: 'SET_DRAGGING_FOLDERS', payload: draggingFolders });
  };

  const setSortOnType = (sortType: string) => {
    dispatch({ type: 'SET_SORT_TYPE', payload: sortType });
  };

  const resetSortOnType = () => {
    dispatch({ type: 'RESET_SORT_TYPE' });
  };

  const setSortOnInvoicePaid = (sortOption: FolderSortInvoiceOptions) => {
    dispatch({ type: 'SET_SORT_ON_INVOICE_PAID', payload: sortOption });
  };

  const resetSortOnInvoicePaid = () => {
    dispatch({ type: 'RESET_SORT_ON_INVOICE_PAID' });
  };

  const setSortOnSignatureStatus = (signatureStatus: SignatureDisplayStatus) => {
    dispatch({ type: 'SET_SORT_ON_SIGNATURE_STATUS', payload: signatureStatus });
  };

  const resetSortOnSignatureStatus = () => {
    dispatch({ type: 'RESET_SORT_ON_SIGNATURE_STATUS' });
  };

  const applyFolderFilters = (folderFilters: FolderFilters) => {
    dispatch({ type: 'SET_FOLDER_FILTERS', payload: folderFilters });
  };

  const updateFolderCategory = async (updatedCategory: DocumentCategoryWithoutReadIdAndClientId) => {
    const categoryFolder = await getFoldersWithCustomFilter(
      (folder) => folder.documentCategoryId === updatedCategory.id && folder.folderEntity === FolderEntity.FOLDER
    ).then((folders) => folders[0]);
    if (categoryFolder) {
      moveFolders([categoryFolder], updatedCategory.parentId ?? '');
    }
  };

  const setListView = (listView: boolean) => {
    dispatch({ type: 'SET_LIST_VIEW', payload: listView });
  };

  const calculateDepthByUrl = async (
    entityDepth: FolderDatabaseModel[],
    folderDepth?: string[],
    newUrlDepth?: string | null
  ) => {
    dispatch({ type: 'SET_ENTITY_DEPTH', payload: entityDepth });
    const folderNavigation: (FolderDatabaseModel | undefined)[] = [];
    if (folderDepth) {
      folderNavigation.push(undefined);
      for (const id of folderDepth) {
        const folder = await getFolderById(id);
        if (folder) {
          folderNavigation.push(folder);
        }
      }
    }

    const baseFolderNavigation = isEmpty(folderNavigation) ? [undefined] : folderNavigation;
    const depth = newUrlDepth === undefined ? query.get('depth') || null : newUrlDepth;
    if (isNil(depth) || !location.href.includes('depth')) {
      dispatch({ type: 'SET_FOLDER_NAVIGATION', payload: baseFolderNavigation });
      return;
    }

    const ids = depth.split('_');
    let entityId: string | undefined = last(baseFolderNavigation)?.entityId;
    let folderIndexToDelete: number | undefined = undefined;
    for (const idIndex in ids) {
      const id = ids[idIndex];
      const entityFolder = await getFoldersWithCustomFilter(
        (folder) => folder.entityId === id && ![FolderEntity.FILE, FolderEntity.FOLDER].includes(folder.folderEntity)
      ).then((folders) => folders[0]);
      if (!isNil(entityFolder)) {
        entityId = id;
        baseFolderNavigation.push(entityFolder);
        continue;
      }
      //Can be a categoryFolder
      const categoryFolder = await getFoldersWithCustomFilter(
        (folder) =>
          folder.entityId === entityId &&
          folder.folderEntity === FolderEntity.FOLDER &&
          folder.documentCategoryId === id
      ).then((folders) => folders[0]);
      if (!isNil(categoryFolder)) {
        baseFolderNavigation.push(categoryFolder);
        continue;
      }

      //Can be an emptyFolder
      const currentFolders = await getCurrentFoldersByDepth(getFolderDepthForNavigation(last(baseFolderNavigation)));
      const newEmptyFolders = calculateEmptyFolders(last(baseFolderNavigation)!, fileCategories, currentFolders);

      const emptyFolder = newEmptyFolders.find((folder) => folder.documentCategoryId === id);
      if (!isNil(emptyFolder)) {
        baseFolderNavigation.push(emptyFolder);
        continue;
      }
      folderIndexToDelete = folderIndexToDelete ?? Number(idIndex);
    }

    if (!isNil(folderIndexToDelete)) {
      ids.splice(folderIndexToDelete);
      history.push(`?depth=${ids.join('_')}`);
    }
    dispatch({ type: 'SET_FOLDER_NAVIGATION', payload: baseFolderNavigation });
  };

  const exportData = async (selectedEntities?: CsvTableType[]) => {
    const baseFolders = await getFoldersByIndex(FolderIndexes.DEPTH, ROOT_FOLDER_ID);
    const input: DownloadFoldersInput = {
      selectedFolders: [
        ...baseFolders.map((folder) => ({
          translatedName: getTranslatedLabel(folder, getTypedLanguage(language)),
          folderDepth: getFolderDepthString(folder, getTypedLanguage(language)),
        })),
      ],
      clientId: clientId!,
      userId: userId!,
      language: language ?? Language.EN,
      onPage: true,
      createCsv: true,
      ...(!isNilOrEmpty(selectedEntities) ? { csvToCreates: selectedEntities } : {}),
    };
    const downloadResult = await mutation<StatusResult, GetDownloadZipFileUrlMutationVariables>(
      GetDownloadZipFileUrlMutation,
      {
        input,
      }
    );

    return { result: downloadResult };
  };

  const setLocalBusy = (localBusy: boolean) => {
    dispatch({ type: 'SET_LOCAL_BUSY', payload: localBusy });
  };

  const value = useMemo(
    () => ({
      ...state,
      getCurrentFoldersByDepth,
      addFolderNavigation,
      setFolderNavigationByFolderId,
      addExternalFiles,
      createNewFolder,
      setUploadFiles,
      deleteFolders,
      changeVisibilityAllFolders,
      setExtendedSearchModel,
      clickOnSearchFolder,
      setSearchFolders,
      cancelSearch,
      cleanUp,
      setIsBusy,
      setOnPage,
      setSelectedFolders,
      renameFolder,
      moveFolders,
      calculateNewFolders,
      updateFolderCategory,
      setDraggingFolders,
      setSortOnType,
      resetSortOnType,
      setSortOnInvoicePaid,
      resetSortOnInvoicePaid,
      setSortOnSignatureStatus,
      resetSortOnSignatureStatus,
      applyFolderFilters,
      setListView,
      calculateDepthByUrl,
      exportData,
      setLocalBusy,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );

  return <FolderWithDBContext.Provider value={value}>{children}</FolderWithDBContext.Provider>;
};

export const useFolderWithDB = () => {
  const context = useContext(FolderWithDBContext);
  if (!context) {
    throw new Error('useFolderWithDB must be used within a FolderWithDBContextProvider');
  }
  return context;
};
